import type { FC } from 'react';
import React, { Fragment, useCallback, useEffect } from 'react';

import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { NativeCollabPresenceStateContainer } from '@confluence/native-collab/entry-points/useNativeCollabPresenceState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import {
	defaultEditLoadingValues,
	useEditPageLoadingValues,
	useEditPageLoadingActions,
} from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';

import { EditPageLoadingComponent } from './EditPageLoadingComponent.tsx';

export const EditPageLoadingListener: FC = () => {
	const { push } = useRouteActions();
	const { contentId, redirectUrl, spaceKey, contentType } = useEditPageLoadingValues();
	const { loadEditor, nativeEditorComponentsPreloader } = useEditPageLoadingActions();
	const isLivePage = useIsCurrentPageLive();

	useEffect(() => {
		const listener = () => {
			loadEditor({ referredFromLivePage: isLivePage });
		};

		window.addEventListener('popstate', listener);

		return () => {
			window.removeEventListener('popstate', listener);
		};
	}, [loadEditor, isLivePage]);

	const redirectToEditor = useCallback(
		(redirectToEditorUrl: string): void => {
			if (
				redirectToEditorUrl &&
				!redirectToEditorUrl.includes('edit-v2') &&
				StatsigConfigurations.getBoolean('confluence_skip_spa_transition_tinymce_config', false)
			) {
				window.location.href = redirectToEditorUrl;
			}
			loadEditor({ ...defaultEditLoadingValues, referredFromLivePage: isLivePage });
			push(redirectToEditorUrl);
		},
		[push, loadEditor, isLivePage],
	);

	return (
		<Fragment>
			{!!redirectUrl && (
				<NativeCollabPresenceStateContainer scope={contentId}>
					<EditPageLoadingComponent
						contentId={contentId}
						spaceKey={spaceKey}
						redirectUrl={redirectUrl}
						redirectToEditor={redirectToEditor}
						nativeEditorComponentsPreloader={nativeEditorComponentsPreloader}
						contentType={contentType}
					/>
				</NativeCollabPresenceStateContainer>
			)}
		</Fragment>
	);
};
